import { useState, useContext } from 'react'
import {
  makeStyles,
  Paper,
  Grid,
  Chip,
  useMediaQuery,
  Divider,
  Tooltip,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from '@material-ui/core'
import ButtonSp from '../ui/ButtonSp'
import clsx from 'clsx'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CourierDetailModal from './CourierDetailModal'
import CheckpriceErrorText from '../utility/CheckpriceErrorText'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import striptags from 'striptags'
import { Context } from '../../context'
import { getWlhByBoxSize } from '../utility/boxSizeUtility'

const { publicRuntimeConfig } = getConfig()

const LLM_SERVICE_TYPES_PROD = [
  'MOTORCYCLE',
  '4_DOOR_PICKUP_TRUCK',
  'CAR',
  'LD_4_DOOR_PICKUP_TRUCK',
  'LD_PICKUP_TRUCK',
  'MOTORCYCLE_RENTAL',
  'MPV',
  'MPV_INTERCITY',
  'PICK_UP_TRUCK',
  'SEDAN_INTERCITY',
  'SUV',
  'SUV_INTERCITY',
  'TRUCK175',
  'TRUCK330',
]

const LLM_SERVICE_TYPES_DEV = [
  'MOTORCYCLE',
  'CAR',
  'MPV',
  'PICK_UP_TRUCK',
  'SUV',
  'TRUCK175',
  'TRUCK330',
]

const Col = ({ children, rightBorder, justifyCenter, classes }) => (
  <Grid
    container
    item
    xs={2}
    className={clsx(
      classes.col,
      rightBorder && classes.rightBorder,
      justifyCenter && classes.justifyCenter
    )}
    direction="column"
  >
    {children}
  </Grid>
)

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4),
    position: 'relative',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    ...theme.homeStyle.boxShadow,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
    },
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  label: {
    fontSize: 14,
    color: '#BDBDBD',
    marginBottom: theme.spacing(1),
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left',
      marginTop: 0,
    },
  },
  data: {
    fontSize: 16,
    width: '100%',
    wordWrap: 'normal',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'right',
    },
    [theme.breakpoints.up('md')]: {
      width: '95%', // to not too close to margin
    },
  },
  price: {
    color: theme.palette.primary.main,
    fontSize: 20,
    margin: `${theme.spacing(0.5)}px 0 ${theme.spacing(2)}px`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  rightBorder: {
    borderRight: '1px solid #E0E0E0',
  },
  justifyCenter: {
    justifyContent: 'center',
  },
  button: {
    height: 50,
    margin: `${theme.spacing(1)}px 0px`,
  },
  remarkText: {
    margin: 0,
    color: theme.palette.error.main,
    fontSize: 12,
    marginTop: theme.spacing(2.25),
    marginLeft: theme.spacing(2.5),
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginLeft: 'unset',
    },
  },
  dropoffChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    // Force the label span to brand blue. MUI's `.MuiChip-label` sets its own
    // color (text.primary ≈ #404041, near-black); depending on stylesheet
    // injection order it could win over the root `color` above and render the
    // text black on some cards. `.dropoffChip .MuiChip-label` has higher
    // specificity so brand blue (#009EFF) always wins.
    '& .MuiChip-label': {
      color: theme.palette.primary.main,
    },
    '& > svg': {
      color: theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      '& > svg': {
        color: theme.palette.primary.main,
      },
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
    },
  },
  recommended: {
    backgroundColor: '#EB5757',
    color: 'white',
    position: 'absolute',
    top: 0,
    left: 0,
    padding: '5px 12px',
    borderBottomRightRadius: 5,
    borderTopLeftRadius: 5,
    fontSize: 12,
  },
  logo: {
    width: 132,
    height: 60,
    margin: `0px 0px ${theme.spacing(0)}px`,
    maxWidth: '100%',
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      margin: `-${theme.spacing(1.5)}px`, // to handle white space around the image file
      marginBottom: theme.spacing(0),
    },
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  flexRowSpaceBetween: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5),
  },
  m3: {
    margin: 3,
  },
  logoAndDetailCol: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 132,
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
    },
  },
  chip: {
    backgroundColor: '#f2f2f2',
  },
  divider: {
    backgroundColor: '#f2f2f2',
    margin: `${theme.spacing(1)}px 0px`,
  },
  displayNone: {
    display: 'none',
  },
  priceLabel: {
    textAlign: 'right',
  },
  recommendedSpacer: {
    height: theme.spacing(1.5),
  },
  serviceTypeSelect: {
    fontSize: 12,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    '& .MuiSelect-select': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
  },
  // --- Courier standard-price ("ราคา Standard ขนส่ง") support -------------
  // ALL of the classes below are applied ONLY when the optional
  // `standardPrice` prop is supplied (i.e. the ?new page). The old
  // /checkprice page passes nothing, so none of these ever attach and its
  // markup/metrics are byte-for-byte unchanged.
  //
  // Tabular figures keep the two stacked numbers on a common digit grid so
  // they don't jitter between results. Applied to the SHIPPOP price only in
  // the new-design path for the same reason (old page keeps proportional
  // figures — changing them there would be a visual change).
  tabularNums: {
    fontVariantNumeric: 'tabular-nums',
  },
  // Pull the struck price up directly beneath the blue SHIPPOP price; the
  // bottom spacing the price used to own moves onto `standardPrice` below so
  // the overall block height stays stable.
  priceWithStandard: {
    marginBottom: theme.spacing(0.25),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.25),
    },
  },
  // The struck standard price. #666666 on the white card = 5.74:1 contrast
  // (WCAG AA needs 4.5:1). NOTE: #999 — the lighter grey used for the column
  // labels — measures only 2.85:1 and would FAIL, so it is deliberately not
  // used here.
  // Self-qualified with the element's own `data-sp-price="standard"`
  // attribute. That keeps this rule at (class + attribute) specificity, so a
  // page-level reskin cannot repaint the struck price with a plain descendant
  // selector regardless of JSS injection order — which is exactly what
  // happened on desktop when CheckpriceNew's `...p:last-child` rule matched
  // this element and rendered it brand-blue/bold instead of muted grey.
  standardPrice: {
    '&[data-sp-price="standard"]': {
      fontSize: 16,
      lineHeight: 1.2,
      fontWeight: 400,
      color: '#666666',
      textDecoration: 'line-through',
      fontVariantNumeric: 'tabular-nums',
      margin: `0 0 ${theme.spacing(2)}px`,
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: 14,
        textAlign: 'right',
        marginBottom: theme.spacing(1),
      },
    },
  },
  // Rows whose courier publishes no (or no cheaper) standard price still
  // render the element, just invisibly — that keeps every card exactly the
  // same height, so the list has no ragged edges and nothing reflows.
  standardPricePlaceholder: {
    visibility: 'hidden',
  },
  // Screen-reader-only text: colour + strikethrough alone must not be the
  // only thing conveying "this is the normal price".
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}))

const ResultCard = ({
  courierImg,
  courierName: courierNameProp,
  condition = 1,
  timeMin,
  timeMax,
  pickupType = '-',
  price = '-',
  remark,
  recommended = true,
  error = false,
  courierCondition,
  courierDetail,
  hidden = false,
  durationString,
  deliveryType,
  courierCode,
  onServiceTypeChange,
  selectedLlmServiceType,
  // Optional new-design-only overrides — all default to the exact old
  // behavior (t('select-courier') label, CSS-class red ribbon, brand-blue
  // courier-info chip) so the old /checkprice page (which never passes these)
  // renders unchanged. Used by components/pages-new/CheckpriceNew.js to show
  // "ส่งพัสดุ" / a pink ribbon / a black "ดูข้อมูลขนส่ง" chip per the new
  // design mockup without forking this shared component.
  actionLabel,
  ribbonColor,
  courierChipColor,
  // Courier's own standard price, struck through beneath the SHIPPOP price.
  // TRI-STATE, and the distinction matters:
  //   undefined -> feature OFF (the old /checkprice page passes nothing):
  //                nothing is rendered and no space is reserved, so that page
  //                is completely unchanged.
  //   null      -> feature ON, this courier has no standard price: the row is
  //                still reserved (invisible) so all cards stay equal height.
  //   number    -> feature ON with data: shown only if it beats the SHIPPOP
  //                price (see `showStandardPrice`).
  // The ?new page passes `data.standardPrice ?? null`, so it is never
  // accidentally `undefined` there.
  standardPrice,
  // Origin/dest/parcel of the quote (lastFetchBody.data[0]: {from,to,parcel}),
  // passed only by the ?new checkprice page so "ส่งพัสดุ" can hand the order off
  // to app /shipment/create/ prefilled. Undefined on the old page -> falls back
  // to router.query, and if neither is present the button still works (empty form).
  shipmentHandoff,
}) => {
  const theme = useTheme()
  const router = useRouter()
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'))
  const [isCourierDetailModalOpen, setCourierDetailModalOpen] = useState(false)
  const isProduction = publicRuntimeConfig.shippopMkpService?.includes('.com')
  const llmServiceTypes = isProduction ? LLM_SERVICE_TYPES_PROD : LLM_SERVICE_TYPES_DEV
  const classes = useStyles()

  const { onRouting } = useContext(Context)

  const { t } = useTranslation('checkprice')

  // Fallback to the translated error title when no courier name is supplied
  // (error rows). Old default was the hardcoded Thai 'เกิดข้อผิดพลาด'.
  const courierName = courierNameProp || t('checkpriceForm.errorTitle')

  const renderCourier = () => (
    <>
      <div className={classes.logoAndDetailCol}>
        <img src={courierImg} alt={courierName} className={classes.logo} />
        <Chip
          label={t('courier-info')}
          onDelete={() => setCourierDetailModalOpen(true)}
          deleteIcon={
            <ExpandMoreIcon
              style={courierChipColor ? { color: courierChipColor } : undefined}
            />
          }
          size="small"
          classes={{
            root: classes.chip,
          }}
          // Inline style wins over CheckpriceNew's `& .MuiChip-root` blue rule,
          // so only this courier-info chip turns black (the near-you chip,
          // which never receives this prop, stays brand-blue). Old page passes
          // no prop → undefined → unchanged.
          style={courierChipColor ? { color: courierChipColor } : undefined}
          onClick={() => setCourierDetailModalOpen(true)}
        />
        {/* deleteIcon and onDelete in Chip code in this file have nothing to do with deleting
          it set as deleteIcon so that it appear on rightside of the chip (Mui will put normal icon on leftside) */}
        {courierCode === 'LLM' && (
          <FormControl size="small" fullWidth>
            <Select
              value={selectedLlmServiceType || llmServiceTypes[0]}
              onChange={(e) => {
                if (onServiceTypeChange) onServiceTypeChange(e.target.value)
              }}
              variant="outlined"
              className={classes.serviceTypeSelect}
            >
              {llmServiceTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      <CourierDetailModal
        isOpen={isCourierDetailModalOpen}
        setOpen={setCourierDetailModalOpen}
        courierCondition={courierCondition}
        courierDetail={courierDetail}
        courierLogo={courierImg}
        courierName={courierName}
      />
    </>
  )

  const renderDropoffChip = () => {
    if (deliveryType === 'inter') return null
    return (
      <Chip
        label={t('near-you')}
        className={classes.dropoffChip}
        onClick={() =>
          window.open(`/nearby-dropoff?courier=${courierCode}`, '_blank')
        }
        onDelete={() =>
          window.open(`/nearby-dropoff?courier=${courierCode}`, '_blank')
        }
        deleteIcon={<ChevronRightIcon color="primary" />}
        size="small"
      />
    )
  }

  const renderPrice = () => {
    if (isNaN(price)) return price
    if (price == 0) return '-'
    if (price % 1 === 0) return `${Math.floor(price).toLocaleString()} ${t('baht')}`
    return `${price.toLocaleString()} ${t('baht')}`
  }

  // Feature is engaged as soon as the caller opts in (even with a null value),
  // which is what lets every card reserve the same vertical space.
  const isStandardPriceEnabled = standardPrice !== undefined

  // Only advertise the standard price when it represents a REAL saving.
  // Deliberately excluded:
  //   • equal prices (e.g. ARM 30 vs 30) — striking through an identical
  //     number implies a discount that doesn't exist;
  //   • standard CHEAPER than SHIPPOP (e.g. FLE 27 vs 28) — that would
  //     advertise SHIPPOP as the more expensive option;
  //   • zero / non-numeric / error rows (price '-'), which parse to NaN.
  const shippopPriceValue = parseFloat(price)
  const standardPriceValue = parseFloat(standardPrice)
  const showStandardPrice =
    isStandardPriceEnabled &&
    !error &&
    !isNaN(standardPriceValue) &&
    !isNaN(shippopPriceValue) &&
    standardPriceValue > 0 &&
    shippopPriceValue > 0 &&
    standardPriceValue > shippopPriceValue

  const formatBaht = (value) =>
    value % 1 === 0
      ? `${Math.floor(value).toLocaleString()} ${t('baht')}`
      : `${value.toLocaleString()} ${t('baht')}`

  // Always rendered once the feature is on — visible for real savings, hidden
  // (but still occupying its line) otherwise, so card heights never differ.
  const renderStandardPrice = () => {
    if (!isStandardPriceEnabled) return null
    return (
      <p
        data-sp-price="standard"
        className={clsx(
          classes.standardPrice,
          !showStandardPrice && classes.standardPricePlaceholder
        )}
        aria-hidden={!showStandardPrice}
      >
        {showStandardPrice ? (
          <>
            <span className={classes.srOnly}>{t('standard-price-label')} </span>
            {formatBaht(standardPriceValue)}
          </>
        ) : (
          ' '
        )}
      </p>
    )
  }

  const renderCondition = () => {
    // if condition is string means that is error message
    if (isNaN(condition))
      return <span style={{ color: 'red' }}>{t(condition)}</span>
    // if there are error, show the error message as condition
    if (error)
      return (
        <span style={{ color: 'red' }}>
          <CheckpriceErrorText errorCode={error} />
        </span>
      )
    // else if condition is a number
    return t('courier-errors.ERR_MIN_ORDER_X', { x: condition })
  }

  const renderReceiveDate = () => {
    if (durationString) {
      if (/^\d+ - \d+$/.test(durationString))
        return `${durationString} ${t('days')}`
      if (!isNaN(durationString)) return `${durationString} ${t('days')}`
      else return durationString
    } else {
      return t('undefined')
    }
  }

  // Build the /shipment/create/ autofill query from the quote's origin/dest/parcel.
  // Prefers the threaded shipmentHandoff (works in fetch mode where the URL is only
  // shallow-updated); otherwise reads the checkprice URL query and resolves the box
  // size to w/l/h. Field remap mirrors DomesticForm's fetchBody:
  //   from.district = ตำบล(subdist), from.state = อำเภอ(dist), from.province = จังหวัด.
  const buildHandoffQuery = () => {
    const params = new URLSearchParams()
    const add = (k, v) => {
      if (v !== undefined && v !== null && v !== '' && v !== '-') params.append(k, v)
    }
    if (shipmentHandoff && shipmentHandoff.from && shipmentHandoff.to) {
      const { from, to, parcel } = shipmentHandoff
      add('subdist_from', from.district)
      add('dist_from', from.state)
      add('prov_from', from.province)
      add('postcode_from', from.postcode)
      add('subdist_to', to.district)
      add('dist_to', to.state)
      add('prov_to', to.province)
      add('postcode_to', to.postcode)
      if (parcel) {
        add('weight', parcel.weight)
        add('width', parcel.width)
        add('length', parcel.length)
        add('height', parcel.height)
      }
    } else {
      const q = (router && router.query) || {}
      add('subdist_from', q.subdist_from)
      add('dist_from', q.dist_from)
      add('prov_from', q.prov_from)
      add('postcode_from', q.postcode_from)
      add('subdist_to', q.subdist_to)
      add('dist_to', q.dist_to)
      add('prov_to', q.prov_to)
      add('postcode_to', q.postcode_to)
      add('weight', q.weight)
      const wlh = q.size_parcel ? getWlhByBoxSize(q.size_parcel) : null
      if (wlh && wlh !== 'blankWlh') {
        add('width', wlh.width)
        add('length', wlh.length)
        add('height', wlh.height)
      }
    }
    add('courier_code', courierCode)
    return params.toString()
  }

  const onUseServiceClick = () => {
    if (deliveryType === 'inter') {
      onRouting(`${publicRuntimeConfig.inter}/create-shipment`)
    } else {
      // pass the query separately so handleRouting joins it with `?` (and can
      // still safely append its own shippop_app/old flags with `&`)
      onRouting(`${publicRuntimeConfig.appShippop}/shipment/create/`, buildHandoffQuery())
    }
  }

  const renderDesktopCardContent = () => (
    <>
      <Grid container>
        <Col justifyCenter classes={classes}>
          {renderCourier()}
        </Col>
        <Col rightBorder classes={classes}>
          <p className={classes.label}>{t('conditions')}</p>
          <p className={classes.data}>{renderCondition()}</p>
        </Col>
        <Col rightBorder classes={classes}>
          <Tooltip title={t('delivery-date-tooltip')} arrow placement="top">
            <p className={classes.label}>{t('estimated-delivery-date')} ⓘ</p>
          </Tooltip>
          <p className={classes.data}>{renderReceiveDate()}</p>
        </Col>
        <Col rightBorder classes={classes}>
          <p className={classes.label}>{t('pickup-location')}</p>
          <p className={classes.data}>{t(pickupType)}</p>
          {['drop-off', 'pick-up-or-drop-off'].includes(pickupType) &&
            renderDropoffChip()}
        </Col>
        <Col classes={classes}>
          <p className={classes.label}>{t('price')}</p>
          <p
            // Stable styling hook for the SHIPPOP price. CheckpriceNew.js's
            // reskin targets this attribute instead of the cell's last <p>,
            // so adding/removing the standard-price line below can never
            // steal the brand-blue/bold treatment. Inert on the old page.
            data-sp-price="shippop"
            className={clsx(
              classes.data,
              classes.price,
              isStandardPriceEnabled && classes.tabularNums,
              isStandardPriceEnabled && classes.priceWithStandard
            )}
          >
            {renderPrice()}
          </p>
          {renderStandardPrice()}
        </Col>
        <Col justifyCenter classes={classes}>
          <ButtonSp
            className={classes.button}
            fullWidth
            disabled={!!error}
            onClick={onUseServiceClick}
          >
            {actionLabel || t('select-courier')}
          </ButtonSp>
        </Col>
      </Grid>
      {remark && <p className={classes.remarkText}>{striptags(remark)}</p>}
      {recommended && (
        <div
          className={classes.recommended}
          style={ribbonColor ? { background: ribbonColor } : undefined}
        >
          {t('recommended-for-you')}
        </div>
      )}
    </>
  )

  const renderMobileCardContent = () => (
    <>
      {recommended && (
        <>
          <div
            className={classes.recommended}
            style={ribbonColor ? { background: ribbonColor } : undefined}
          >
            {t('recommended-for-you')}
          </div>
          <div className={classes.recommendedSpacer} />
        </>
      )}
      <div className={classes.flexCol}>
        <div className={classes.flexRowSpaceBetween}>
          {renderCourier()}
          <div>
            <p className={clsx(classes.label, classes.priceLabel)}>
              {t('price')}
            </p>
            <p
              data-sp-price="shippop"
              className={clsx(
                classes.data,
                classes.price,
                isStandardPriceEnabled && classes.tabularNums,
                isStandardPriceEnabled && classes.priceWithStandard
              )}
            >
              {renderPrice()}
            </p>
            {renderStandardPrice()}
          </div>
        </div>
        <Divider classes={{ root: classes.divider }} />
        <div className={classes.flexRowSpaceBetween}>
          <p className={clsx(classes.label, classes.m3)}>{t('conditions')}</p>
          <p className={clsx(classes.data, classes.m3)}>{renderCondition()}</p>
        </div>
        <div className={classes.flexRowSpaceBetween}>
          <Tooltip
            title={t('delivery-date-tooltip')}
            arrow
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={30000}
          >
            <p className={clsx(classes.label, classes.m3)}>
              {t('estimated-delivery-date')} ⓘ
            </p>
          </Tooltip>

          <p className={clsx(classes.data, classes.m3)}>
            {renderReceiveDate()}
          </p>
        </div>
        <div className={classes.flexRowSpaceBetween}>
          <p className={clsx(classes.label, classes.m3)}>
            {t('pickup-location')}
          </p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <p className={clsx(classes.data, classes.m3)}>{t(pickupType)}</p>
            {['drop-off', 'pick-up-or-drop-off'].includes(pickupType) &&
              renderDropoffChip()}
          </div>
        </div>
        <ButtonSp
          className={classes.button}
          fullWidth
          disabled={error}
          onClick={onUseServiceClick}
        >
          {actionLabel || t('select-courier')}
        </ButtonSp>
        {remark && <p className={classes.remarkText}>{striptags(remark)}</p>}
      </div>
    </>
  )

  return (
    <Paper className={clsx(classes.paper, hidden && classes.displayNone)}>
      {isTablet ? renderMobileCardContent() : renderDesktopCardContent()}
    </Paper>
  )
}

export default ResultCard
